import { Button, Table, Tag } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import {
  ActivityLogAction,
  CUSTOMER_CONFIG_LABELS,
} from 'modules/activity-log-service/constants'
import { useActivityQueriesInfinite } from 'modules/activity-log-service/hooks/useActivityQuery/useActivityQueriesInfinite'
import React, { memo, useMemo } from 'react'
import type { ApiActivity } from 'types/activity'

const PAGE_SIZE = 10

const SETTING_KEYS = Object.keys(CUSTOMER_CONFIG_LABELS)

interface ConfigurationChange {
  label: string
  oldValue?: boolean
  newValue?: boolean
}

function toBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

/** Reads the changed setting + old/new values from a log entry's additionalData. */
export function getConfigurationChange(
  activity: ApiActivity
): ConfigurationChange {
  const data = (activity.additionalData ?? {}) as Record<string, any>
  const oldData = (data.oldData ?? {}) as Record<string, unknown>
  const newData = (data.newData ?? {}) as Record<string, unknown>

  const settingKey: string | undefined =
    typeof data.settingKey === 'string'
      ? data.settingKey
      : SETTING_KEYS.find((key) => key in oldData || key in newData)

  const label =
    (settingKey &&
      (CUSTOMER_CONFIG_LABELS as Record<string, string>)[settingKey]) ||
    data.settingLabel ||
    settingKey ||
    '—'

  return {
    label,
    oldValue: settingKey ? toBoolean(oldData[settingKey]) : undefined,
    newValue: settingKey ? toBoolean(newData[settingKey]) : undefined,
  }
}

function OnOffTag({ value }: { value?: boolean }) {
  if (value === undefined) return <span>—</span>
  return <Tag color={value ? 'green' : 'red'}>{value ? 'ON' : 'OFF'}</Tag>
}

const columns: ColumnsType<ApiActivity> = [
  {
    title: 'When',
    key: 'timestamp',
    width: 180,
    render: (_: unknown, activity) =>
      activity.timestamp
        ? dayjs(activity.timestamp).format('MMM D, YYYY h:mm A')
        : '—',
  },
  {
    title: 'Changed by',
    key: 'actor',
    width: 200,
    render: (_: unknown, activity) => activity.actorName || '—',
  },
  {
    title: 'Setting',
    key: 'setting',
    render: (_: unknown, activity) => getConfigurationChange(activity).label,
  },
  {
    title: 'Change',
    key: 'change',
    width: 160,
    render: (_: unknown, activity) => {
      const { oldValue, newValue } = getConfigurationChange(activity)
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <OnOffTag value={oldValue} />
          <span aria-hidden>→</span>
          <OnOffTag value={newValue} />
        </span>
      )
    },
  },
]

/**
 * History of Customer Configuration toggle changes (EMS-9960): who turned which
 * setting ON/OFF for this customer, newest first.
 */
function CustomerConfigurationHistory({ customerId }: { customerId: string }) {
  const filter = useMemo(
    () => ({
      targetIds: [customerId],
      action: [ActivityLogAction.UPDATE_CUSTOMER_CONFIGURATION],
      limit: PAGE_SIZE,
    }),
    [customerId]
  )

  const {
    allActivities,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useActivityQueriesInfinite(filter, { enabled: Boolean(customerId) })

  if (isError) {
    return (
      <div role="alert">
        <span style={{ marginRight: 8 }}>
          Unable to load configuration history.
        </span>
        <Button size="small" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Table<ApiActivity>
        size="small"
        bordered
        rowKey={(activity) => String(activity.id)}
        columns={columns}
        dataSource={allActivities}
        loading={isLoading || (isFetching && !isFetchingNextPage)}
        pagination={false}
        locale={{ emptyText: 'No configuration changes recorded yet.' }}
      />
      {hasNextPage ? (
        <div style={{ textAlign: 'center' }}>
          <Button
            size="small"
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export default memo(CustomerConfigurationHistory)
